import { useState, type ReactNode } from "react";
import { Can } from "@core/permissions";
import { tokens } from "@core/theme/tokens";
import { CursorTable, tablePadding, type CursorTableColumn } from "@core/table";
import {
  EmptyState,
  FeedbackState,
  ListLayout,
  PrizesHeader,
  RowIconAction,
  type ActiveFilter,
} from "@portal/common";
import { formatShortDate } from "../vehicles/dateFormat";
import type {
  AccountFilter,
  Progress,
  ProgressStatusFilter,
} from "./progressApi";
import { useProgressDetail, useProgressList } from "./progressQueries";
import { GoalValidationModal } from "./GoalValidationModal";
import { ProgressDetailSheet } from "./ProgressDetailSheet";

// Atajo desde la fila (admin.jsx:2081: el chip "Validar" solo aparece en las
// filas con meta alcanzada). SUB-001 exige mostrar la evidencia de compras
// antes de validar, así que el atajo carga el detalle y abre el mismo modal
// de validación de dos pasos, en vez de saltarse esa evidencia.
function QuickValidateAction({ progressId }: { progressId: string }) {
  const [open, setOpen] = useState(false);
  const detail = useProgressDetail(open ? progressId : null);
  return (
    <>
      <RowIconAction
        label="Validar"
        icon="check"
        tone="accent"
        onClick={() => setOpen(true)}
      />
      {open && detail.data ? (
        <GoalValidationModal
          progress={detail.data}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

const STATUS_LABEL: Record<Exclude<ProgressStatusFilter, "all">, string> = {
  // GP-01-A (resolución humana 2026-09-13): etiquetas de `pending` y
  // `expired` fijadas por el humano — SPEC-209 no las nombra, así que no se
  // redactan distinto a como quedaron asentadas ahí.
  pending: "Pendiente",
  expired: "Vencido",
  in_progress: "En curso",
  goal_reached: "Meta alcanzada",
  prize_delivered: "Ganador entregado",
  closed_on_inactivation: "Cerrado por baja",
};

const inputStyleSelect = {
  minWidth: 178,
  padding: "11px 13px",
  color: tokens.text.primary,
  background: tokens.surface.surface,
  border: `1px solid ${tokens.surface.line2}`,
  borderRadius: 11,
  font: `400 13.5px ${tokens.font.ui}`,
  cursor: "pointer" as const,
};

function IndicatorCard({
  label,
  value,
  note,
  tone = "neutral",
}: {
  label: string;
  value: number;
  note: string;
  tone?: "neutral" | "gold" | "danger";
}) {
  const color =
    tone === "danger"
      ? tokens.danger.base
      : tone === "gold"
        ? tokens.gold.light
        : tokens.text.primary;
  // Mismo gradiente diagonal que `Kpi` (admin.jsx:1381-1391) para las
  // variantes `alerta`/`gold`; la variante neutral conserva el fondo plano.
  const background =
    tone === "danger"
      ? "linear-gradient(158deg, rgba(214,124,104,0.12), rgba(214,124,104,0.02) 62%)"
      : tone === "gold"
        ? "linear-gradient(158deg, rgba(159,176,166,0.10), rgba(159,176,166,0.015) 62%)"
        : tokens.surface.surface;
  return (
    <div
      style={{
        padding: 18,
        // `Card` (admin.jsx:80-81, DESIGN-TOKENS.md): radio 18, distinto del
        // `tokens.radius.sm` (15) usado por otros paneles de esta pantalla.
        borderRadius: 18,
        border: `1px solid ${
          tone === "danger"
            ? tokens.danger.line
            : tone === "gold"
              ? tokens.gold.line
              : tokens.surface.line
        }`,
        background,
      }}
    >
      <div
        className="label"
        style={{
          marginBottom: 10,
          color: tone === "danger" ? tokens.danger.base : undefined,
        }}
      >
        {label}
      </div>
      <div
        className="display"
        style={{
          fontSize: 34,
          lineHeight: 1.06,
          color,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      <div
        style={{
          font: `400 11px/1.45 ${tokens.font.ui}`,
          color: tokens.text.disabled,
          marginTop: 7,
        }}
      >
        {note}
      </div>
    </div>
  );
}

export function GrandPrizeProgressScreen() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ProgressStatusFilter>("all");
  const [account, setAccount] = useState<AccountFilter>("all");
  const [detailId, setDetailId] = useState<string | null>(null);

  const query = { status, account, search };
  const progress = useProgressList(query);
  const pages = progress.data?.pages ?? [];
  const rows = pages.flatMap((page) => page.items);
  const lastPage = pages.at(-1);
  const indicators = pages[0]?.indicators;
  const total = lastPage?.meta.total ?? rows.length;

  const activeFilters: ActiveFilter[] = [];
  if (status !== "all") {
    activeFilters.push({
      key: "status",
      label: `Estado del avance · ${STATUS_LABEL[status]}`,
      onClear: () => setStatus("all"),
    });
  }
  if (account !== "all") {
    activeFilters.push({
      key: "account",
      label: `Estado de cuenta · ${account === "activated" ? "Activado" : "No activado"}`,
      onClear: () => setAccount("all"),
    });
  }
  if (search.trim()) {
    activeFilters.push({
      key: "search",
      label: `Búsqueda · ${search.trim()}`,
      onClear: () => setSearch(""),
    });
  }

  // Orden y anchos calcados de `COLS` en `GranPremioCiclos` (admin.jsx:1540:
  // 'minmax(180px,1.4fr) 116px 126px minmax(140px,1fr) minmax(126px,1fr) 186px'
  // + 268px de acciones). El orden de columnas es Miembro · Cuenta · Inicio
  // del conteo · Cuenta desde · Avance · Estado del avance.
  const columns: CursorTableColumn<Progress>[] = [
    {
      key: "member",
      header: "Miembro",
      width: "minmax(180px,1.4fr)",
      // admin.jsx:2072 (`GranPremioCiclos`): avatar de iniciales + nombre,
      // mismo patrón `className="av"` que MembersScreen.tsx:478.
      render: (row) => {
        const name = row.member.legalName ?? "—";
        const initials = name
          .split(/\s+/)
          .slice(0, 2)
          .map((part) => part[0])
          .join("")
          .toUpperCase();
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
            }}
          >
            <div
              className="av"
              style={{ width: 28, height: 28, fontSize: 10, flex: "none" }}
            >
              {initials}
            </div>
            <span
              style={{
                font: `600 12.5px ${tokens.font.ui}`,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {name}
            </span>
          </div>
        );
      },
    },
    {
      key: "account",
      header: "Cuenta",
      width: "116px",
      // Mismas clases y tonos que `GranPremioCiclos` (admin.jsx:2027: `stat
      // stat-` + (Activado ? 'silver' : 'neutral')). RF-PRE-11 pide resaltar
      // la cuenta no activada; el indicador "Sobre cuentas no activadas" de
      // la cabecera y el filtro "Estado de cuenta" ya cumplen ese objetivo
      // sin reestilar el chip — el prototipo no le da un tono de alerta acá,
      // y esta pantalla no reestila (uncertainty para design-reviewer).
      render: (row) => (
        <span
          className={`stat stat-${row.member.accountActivated ? "silver" : "neutral"}`}
        >
          {row.member.accountActivated ? "Activado" : "Registrado"}
        </span>
      ),
    },
    {
      key: "startsOn",
      header: "Inicio del conteo",
      width: "126px",
      render: (row) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatShortDate(row.startsOn)}
        </span>
      ),
    },
    {
      key: "countsFrom",
      header: "Cuenta desde",
      width: "minmax(140px,1fr)",
      render: (row) => (
        <span
          style={{ fontVariantNumeric: "tabular-nums" }}
          title="El mayor entre el inicio del conteo y hoy menos la ventana."
        >
          {formatShortDate(row.countsFrom)}
        </span>
      ),
    },
    {
      key: "progress",
      header: "Avance",
      width: "minmax(126px,1fr)",
      // admin.jsx MiniProg: barra + "n/meta". Reutiliza las clases CSS
      // `.prog`/`.prog-gold` ya usadas en GrandPrizeConfigScreen.tsx:642.
      render: (row) => {
        const reached = row.progress >= row.goal;
        const pct = Math.min(
          100,
          Math.round((row.progress / Math.max(1, row.goal)) * 100),
        );
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              minWidth: 0,
            }}
          >
            <div
              className={`prog${reached ? " prog-gold" : ""}`}
              style={{ flex: 1, minWidth: 54, height: 5 }}
            >
              <i style={{ width: `${pct}%` }} />
            </div>
            <span
              style={{
                font: `600 11.5px ${tokens.font.ui}`,
                color: reached ? tokens.gold.light : tokens.text.secondary,
                fontVariantNumeric: "tabular-nums",
                flex: "none",
              }}
            >
              {row.progress}/{row.goal}
            </span>
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Estado del avance",
      width: "186px",
      // Mismas clases/tonos que `CicloChip` (admin.jsx:1352-1363): silver
      // (en curso), gold-strong (meta alcanzada, con el mismo sufijo "· por
      // validar" del prototipo — acá se distingue además la ya validada),
      // gold (ganador entregado). "Cerrado por baja" usa el estilo `EXPIRADA`
      // (admin.jsx:626): mayúsculas, `letterSpacing` 0.12em, tono danger.
      render: (row) => {
        if (row.status === "closed_on_inactivation") {
          return (
            <span
              style={{
                font: `600 10.5px/1 ${tokens.font.ui}`,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                borderRadius: 999,
                padding: "6px 11px",
                display: "inline-flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                background: tokens.danger.dim,
                color: tokens.danger.base,
                border: `1px solid ${tokens.danger.line}`,
              }}
            >
              {STATUS_LABEL[row.status]}
            </span>
          );
        }
        const statClass =
          row.status === "in_progress"
            ? "stat-silver"
            : row.status === "goal_reached"
              ? "stat-gold-strong"
              : "stat-gold";
        return (
          <span className={`stat ${statClass}`}>
            {row.status === "goal_reached"
              ? `Meta alcanzada · ${row.validatedAt ? "validada" : "por validar"}`
              : STATUS_LABEL[row.status]}
          </span>
        );
      },
    },
  ];

  let body: ReactNode;
  if (progress.isLoading) {
    body = <FeedbackState kind="loading" />;
  } else if (progress.isError) {
    body = <FeedbackState kind="error" />;
  } else if (rows.length === 0) {
    body = (
      <EmptyState kind={activeFilters.length ? "no-results" : "no-data"} />
    );
  } else {
    body = (
      <CursorTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        page={{ nextCursor: lastPage?.meta.next ?? null, totalCount: total }}
        onLoadMore={() => progress.fetchNextPage()}
        isLoadingMore={progress.isFetchingNextPage}
        headerPadding={tablePadding("18px", "11px")}
        rowPadding={tablePadding("12px")}
        rowFont={`400 12.5px ${tokens.font.ui}`}
        // Piso de la grilla = suma de los mínimos de las 6 columnas (180+116+
        // 126+140+126+186 = 874) + 6 separaciones de 14 + 48 de padding + 86
        // de acciones (ojo + validar: 2 × 40 + 6) = 1092. Antes 1200 (con 268
        // de acciones) superaba el contenedor de ~1102 a 1440x900 y empujaba
        // la acción de fila fuera de la vista; con 1092 la tabla cabe y el
        // ojo queda visible sin scroll horizontal.
        minWidth={1092}
        actionWidth="86px"
        // Revisión en vivo 2026-09-22, punto #10: las tres acciones de esta
        // fila eran hermanas y ninguna se parecía a la otra — "Ver detalle"
        // era `.btn-link` (texto plano), "Ajustar inicio" un `.chip` neutro y
        // "Validar" un `.chip` champagne. Era la evidencia más directa de que
        // no había un patrón único de "esto es una acción". Las tres pasan a
        // ícono + tooltip; "Validar" conserva su acento champagne con el tono
        // `accent`, que es el mismo par de tokens que tenía a mano.
        rowActions={(row) => (
          <>
            <RowIconAction
              label="Ver detalle"
              icon="eye"
              onClick={() => setDetailId(row.id)}
            />
            {row.status === "goal_reached" && !row.validatedAt ? (
              <Can module="prizes" action="canEdit">
                <QuickValidateAction progressId={row.id} />
              </Can>
            ) : null}
          </>
        )}
      />
    );
  }

  return (
    <div>
      <PrizesHeader />
      {/* AdminPremios (admin.jsx:2626-2632): la fila de tabs ya cierra con
          `marginBottom: 22` + `paddingBottom: 18` (PrizesTabsRow.tsx:432-433);
          `GranPremioCiclos` no agrega otro margen encima de la grilla de
          KPIs. Un `marginTop` acá duplicaba ese espacio y corría toda la
          tabla ~5px hacia abajo contra el canónico. */}
      <div>
        {indicators ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 14,
              marginBottom: 18,
            }}
          >
            <IndicatorCard
              label="Avances activos"
              value={indicators.activeCount}
              note="Miembros compitiendo por el gran premio"
            />
            <IndicatorCard
              label="Sobre cuentas no activadas"
              value={indicators.onUnactivatedAccounts}
              note="El avance corre sin que el miembro haya activado su cuenta"
              tone="danger"
            />
            <IndicatorCard
              label="Próximos a la meta"
              value={indicators.nearGoal}
              note="A una o dos compras de alcanzarla"
            />
            <IndicatorCard
              label="Metas por validar"
              value={indicators.goalsToValidate}
              note="Requieren validación del administrador"
              tone="gold"
            />
            <IndicatorCard
              label="Ganadores entregados"
              value={indicators.prizesDelivered}
              note="Histórico del programa"
            />
          </div>
        ) : null}

        <ListLayout
          search={
            <>
              <input
                aria-label="Buscar por miembro"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por miembro"
                style={{
                  flex: 1,
                  minWidth: 230,
                  maxWidth: 340,
                  padding: "11px 13px",
                  color: tokens.text.primary,
                  background: tokens.surface.surface,
                  border: `1px solid ${tokens.surface.line2}`,
                  borderRadius: 11,
                  font: `400 13.5px ${tokens.font.ui}`,
                }}
              />
              <select
                aria-label="Estado del avance"
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value as ProgressStatusFilter)
                }
                style={inputStyleSelect}
              >
                <option value="all">Estado del avance · todos</option>
                {(
                  Object.keys(STATUS_LABEL) as Array<keyof typeof STATUS_LABEL>
                ).map((value) => (
                  <option key={value} value={value}>
                    {STATUS_LABEL[value]}
                  </option>
                ))}
              </select>
              <select
                aria-label="Estado de cuenta"
                value={account}
                onChange={(event) =>
                  setAccount(event.target.value as AccountFilter)
                }
                style={inputStyleSelect}
              >
                <option value="all">Estado de cuenta · todas</option>
                <option value="activated">Activado</option>
                <option value="not_activated">No activado</option>
              </select>
              <div style={{ flex: 1 }} />
              <span
                style={{
                  font: `500 11.5px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                }}
              >
                {rows.length} de {total} avances
              </span>
            </>
          }
          filters={activeFilters}
          {...(activeFilters.length
            ? {
                onClearAllFilters: () => {
                  setStatus("all");
                  setAccount("all");
                  setSearch("");
                },
              }
            : {})}
        >
          {body}
        </ListLayout>
      </div>

      {detailId ? (
        <ProgressDetailSheet
          progressId={detailId}
          onClose={() => setDetailId(null)}
        />
      ) : null}
    </div>
  );
}
